<template>
  <button class="base-btn" :class="btnClass" :type="typedButtonType" :form="form" :disabled="disabled"
    :aria-disabled="disabled">
    <span v-show="!isLoading" class="base-btn__name">{{ name }}</span>
    <div v-show="isLoading" class="base-btn__loader"></div>
    <slot v-if="!name && !isLoading" class="base-btn__slot"> </slot>
    <slot v-if="isIcon" class="base-btn__slot"> </slot>
  </button>
</template>

<script setup>
import { computed } from "vue";
const props = defineProps({
  name: {
    type: String,
    default: "",
  },
  type: {
    type: String,
    default: "button",
    // Accept only valid button types; Vue DOM typings expect this union
    validator: /** @param {unknown} v */ (v) => typeof v === 'string' && (v === 'button' || v === 'submit' || v === 'reset'),
  },
  mode: {
    type: String,
    default: "",
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
  isIcon: {
    type: Boolean,
    default: false,
  },
  iconClass: {
    type: String,
    default: "",
  },
  isSearch: {
    type: Boolean,
    default: false,
  },
  form: {
    type: String,
    default: "",
  },
  color: String,
});

const btnClass = computed(() => {
  return [
    {
      "base-btn_disabled": props.disabled,
      "base-btn_bordered": props.mode === "bordered",
      "base-btn_text": props.mode === "text",
      "base-btn_secondary": props.mode === "secondary",
      "base-btn_is-search": props.isSearch,
      "base-btn_is-loading": props.isLoading,
    },
  ];
});

// Narrow the type prop to a valid union for DOM typings
const typedButtonType = computed(() => (props.type === 'submit' || props.type === 'reset' || props.type === 'button') ? props.type : 'button')
</script>

<style lang="scss" scoped>
@media (prefers-color-scheme: light) {
  .base-btn {
    &__loader {
      color: #fff !important;
    }
  }
  .base-btn__name{
    color: #fff !important
  }
}
.base-btn {
  padding: 5px 10px;
  display: inline-flex;
  justify-content: center;
  align-items: center;
  // background: var(--interactive-01);
  // background: var(--primary-button-bg);
  background: var(--container-head-new);
  border-radius: 4.8px !important;
  border: 1px solid var(--groups-border);
  // color: var(--button-on-primary);
  // border-radius: 8px;
  // color: #09090B;
  color: var(--text-theme-white-black);
  // color: #ffffff;
  min-width: 106px;
  // max-height: 40px;
  // height: 44px;
  max-height: 38px;
  height: 38px;
  font-size: 14px;
  // font-family: 'SFProText', serif;
  font-family: $poppins;
  font-weight: 500;
  transition: all 0.3s ease;
  -webkit-tap-highlight-color: transparent;
  position: relative;

  &:hover {
    // opacity: 0.7;
  }

  &--extra-small {
    max-height: 32px;
    padding: 6px 4px;
  }

  &--small {
    max-height: 40px;
    min-width: 154px;
    padding: 8px 6px;
    font-size: 14px;

    @include _575 {
      font-size: 14px !important;
    }

    @include _380 {
      font-size: 14px !important;
      padding: 8px 6px !important;
    }
  }

  // &__name {
  //   color: white;
  //   display: inline-block;
  //   margin-right: 0;
  //   position: absolute;
  //   left: 50%;
  //   transform: translateX(-50%);
  //   font-weight: 400;
  // }

  // &__loader {
  //   width: 18px;
  //   height: 18px;
  //   color: #ffffff;
  //   border: 2px solid currentcolor;
  //   border-bottom-color: transparent;
  //   border-radius: 50%;
  //   animation: 1s loader linear infinite;
  //   position: absolute;
  //   left: 50%;
  //   top: 50%;
  //   transform: translate(-50%, -50%);
  // }
  &__name {
    color: #000;
    display: inline-block;
    margin-right: 0;
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
    font-weight: 600;
  }

  &__loader {
    width: 18px;
    height: 18px;
    color: #000;
    border: 2px solid currentcolor;
    border-bottom-color: transparent;
    border-radius: 50%;
    animation: 1s loader linear infinite;
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
  }

  @keyframes loader {
    0% {
      transform: translate(-50%, -50%) rotate(0deg);
    }

    100% {
      transform: translate(-50%, -50%) rotate(360deg);
    }
  }

  &__slot {
    text-align: center;
  }

  &_disabled {
    background: var(--background-secondary);
    color: #3F3F46;
    pointer-events: none;
  }

  &_bordered {
    border: 1px solid var(--foreground-primary);
    background: transparent;
    color: var(--foreground-primary);
  }

  &_text {
    padding: 0;
    min-width: auto;
    background: transparent;
    color: var(--interactive-01);
    transition: 0.3s ease-in-out;
  }

  &_secondary {
    background: var(--background-btn-secondary);
    color: var(--foreground-primary);
    transition: 0.3s ease-in-out;
  }

  &_is-search {
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
    margin-left: 15px;
    margin-right: -16px;

    .base-btn__name {
      margin-right: 10px;
    }
  }

  &_is-loading {
    pointer-events: none;
  }

  @include _575 {
    font-size: 18px;
  }

  @include _380 {
    font-size: 16px;
    padding: 10px 15px;
  }
}

/* In dark mode, keep black text on the default green button (e.g., Unfollow)
   without affecting bordered/secondary/text variants */
// [scheme='dark'] 
.base-btn:not(.base-btn_secondary):not(.base-btn_bordered):not(.base-btn_text) {
  // color: #09090B;
  color: var(--text-color-black-white);
}
</style>

